import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigationGuard } from "@/lib/navigation/navigationGuard";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { Theme, ThemeFamily, ThemeStatus } from "@/lib/api/types";
import { THEME_FAMILIES, THEME_NICHES } from "@/lib/api/types";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { Banner, Button, Field, FilterChip, Input, Select, TextArea, useToast } from "@/shared/rd";
import { useUpdateThemeMutation } from "../services/useUpdateThemeMutation";
import { THEME_STATUS_LABEL_KEY } from "./theme-status";

const FAMILY_LABEL_KEY: Record<ThemeFamily, string> = {
  starter: keys.themes.families.starter,
};

const STATUS_VALUES: ThemeStatus[] = ["DRAFT", "PUBLISHED", "ARCHIVED"];

export function ThemeEditForm({ theme }: { theme: Theme }) {
  const t = useTranslations();
  const toast = useToast();
  /** A failed save: the server's own words ("" when it had none). */
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const initialValues = useMemo(() => ({
    family: theme.family,
    status: theme.status,
    niches: theme.niches,
    nameAr: theme.nameAr,
    nameEn: theme.nameEn,
    descriptionAr: theme.descriptionAr ?? "",
    descriptionEn: theme.descriptionEn ?? "",
    sortWeight: String(theme.sortWeight),
  }), [theme.family, theme.status, theme.niches, theme.nameAr, theme.nameEn, theme.descriptionAr, theme.descriptionEn, theme.sortWeight]);
  const syncedDefaults = useRef(initialValues);
  const { register, reset, setValue, getValues, control, formState: { isDirty } } = useForm({
    defaultValues: initialValues,
  });
  const [family, status, niches] = useWatch({
    control,
    name: ["family", "status", "niches"],
  });
  const setDirtySource = useNavigationGuard()?.setDirtySource;
  useEffect(() => {
    if (isDirty || syncedDefaults.current === initialValues) return;
    syncedDefaults.current = initialValues;
    reset(initialValues);
  }, [initialValues, isDirty, reset]);
  useLayoutEffect(() => {
    setDirtySource?.("details", isDirty);
    return () => setDirtySource?.("details", false);
  }, [isDirty, setDirtySource]);
  const { mutateAsync: updateTheme, isPending } = useUpdateThemeMutation();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    const formData = new FormData(event.currentTarget);

    const nameAr = String(formData.get("nameAr") ?? "").trim();
    const nameEn = String(formData.get("nameEn") ?? "").trim();
    const nextFieldErrors: Record<string, string> = {};
    if (nameAr.length === 0) {
      nextFieldErrors.nameAr = t("themes.nameArRequired");
    }
    if (nameEn.length === 0) {
      nextFieldErrors.nameEn = t("themes.nameEnRequired");
    }
    const sortWeightRaw = String(formData.get("sortWeight") ?? "").trim();
    const sortWeight =
      sortWeightRaw.length > 0 ? Number(sortWeightRaw) : undefined;
    if (sortWeight !== undefined && !Number.isFinite(sortWeight)) {
      nextFieldErrors.sortWeight = t("themes.sortWeightNumber");
    }
    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      return;
    }

    const descriptionAr = String(formData.get("descriptionAr") ?? "").trim();
    const descriptionEn = String(formData.get("descriptionEn") ?? "").trim();

    try {
      const updated = await updateTheme({
        themeId: theme.id,
        family,
        status,
        nameAr,
        nameEn,
        descriptionAr,
        descriptionEn,
        niches,
        sortWeight,
      });
      reset({
        family: updated.family,
        status: updated.status,
        niches: updated.niches,
        nameAr: updated.nameAr,
        nameEn: updated.nameEn,
        descriptionAr: updated.descriptionAr ?? "",
        descriptionEn: updated.descriptionEn ?? "",
        sortWeight: String(updated.sortWeight),
      });
    } catch (submitError) {
      setError(submitError instanceof ApiJsonClientError ? submitError.message || "" : "");
      return;
    }

    toast.show({ message: t("themes.savedToast", { name: nameEn || nameAr }) });
  };

  const toggleNiche = (value: string) => {
    // Read the form's current value, not the last render's, so two quick
    // toggles do not overwrite each other.
    const current = getValues("niches");
    setValue(
      "niches",
      current.includes(value) ? current.filter((n) => n !== value) : [...current, value],
      { shouldDirty: true },
    );
  };
  const twoUp = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))", gap: 12 } as const;

  return (
    <form noValidate onSubmit={handleSubmit} style={{ width: "100%", maxWidth: 640 }}>
      <fieldset
        disabled={isPending}
        style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0, margin: 0, padding: 0, border: 0 }}
      >
        {error !== null ? (
          <Banner
            tone="error"
            title={t("themes.saveFailedTitle")}
            actions={
              <Button type="submit" size="sm" loading={isPending} loadingLabel={t(keys.shell.retrying)}>
                {t(keys.shell.retry)}
              </Button>
            }
          >
            {[error, t("themes.nothingChanged")].filter(Boolean).join(" ")}
          </Banner>
        ) : null}
        <div style={twoUp}>
          <Field label={t(keys.themes.family)} htmlFor="theme-edit-family" error={fieldErrors.family}>
            <Select
              id="theme-edit-family"
              aria-label={t(keys.themes.family)}
              value={family}
              onChange={(event) =>
                setValue("family", (event.target.value as ThemeFamily) || "starter", { shouldDirty: true })
              }
              options={[
                ...THEME_FAMILIES.map((value) => ({ value, label: t(FAMILY_LABEL_KEY[value]) })),
                // A seeded family the admin has no label for still shows as itself.
                ...(THEME_FAMILIES.includes(family) ? [] : [{ value: family, label: family }]),
              ]}
            />
          </Field>
          <Field label={t(keys.common.status)} htmlFor="theme-edit-status">
            <Select
              id="theme-edit-status"
              aria-label={t(keys.common.status)}
              value={status}
              onChange={(event) =>
                setValue("status", (event.target.value as ThemeStatus) || "DRAFT", { shouldDirty: true })
              }
              options={STATUS_VALUES.map((value) => ({ value, label: t(THEME_STATUS_LABEL_KEY[value]) }))}
            />
          </Field>
        </div>
        <div style={twoUp}>
          <Field label={t(keys.themes.nameAr)} htmlFor="theme-edit-name-ar" error={fieldErrors.nameAr}>
            <Input id="theme-edit-name-ar" {...register("nameAr")} dir="rtl" invalid={Boolean(fieldErrors.nameAr)} />
          </Field>
          <Field label={t(keys.themes.nameEn)} htmlFor="theme-edit-name-en" error={fieldErrors.nameEn}>
            <Input id="theme-edit-name-en" {...register("nameEn")} dir="ltr" invalid={Boolean(fieldErrors.nameEn)} />
          </Field>
        </div>
        <div style={twoUp}>
          <Field label={t(keys.themes.descriptionAr)} htmlFor="theme-edit-desc-ar">
            <TextArea id="theme-edit-desc-ar" {...register("descriptionAr")} dir="rtl" rows={3} />
          </Field>
          <Field label={t(keys.themes.descriptionEn)} htmlFor="theme-edit-desc-en">
            <TextArea id="theme-edit-desc-en" {...register("descriptionEn")} dir="ltr" rows={3} />
          </Field>
        </div>
        <Field label={t(keys.themes.niches)} help={t(keys.themes.nichesPlaceholder)}>
          <div role="group" aria-label={t(keys.themes.niches)} style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {THEME_NICHES.map((value) => (
              <FilterChip key={value} value={value} active={niches.includes(value)} onClick={() => toggleNiche(value)}>
                {t(keys.themes.nicheLabels[value])}
              </FilterChip>
            ))}
          </div>
        </Field>
        <Field label={t(keys.themes.sortWeight)} htmlFor="theme-edit-sort-weight" error={fieldErrors.sortWeight}>
          <Input
            id="theme-edit-sort-weight"
            {...register("sortWeight")}
            type="number"
            inputMode="numeric"
            dir="ltr"
            invalid={Boolean(fieldErrors.sortWeight)}
            style={{ width: 120 }}
          />
        </Field>
        <div>
          <Button type="submit" variant="primary" loading={isPending} loadingLabel={t("themes.saving")}>
            {t(keys.common.update)}
          </Button>
        </div>
      </fieldset>
    </form>
  );
}
